import { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchNewsList, fetchNewsReport, deleteNewsReport } from "../api";
import { fmtTs } from "../utils/time";

const CVE_RE = /\bCVE-\d{4}-\d{4,}\b/g;

// Sentinel used to mark CVE-icon-link placements in masked text.
// react-markdown will see e.g. "CVE-2026-1234 [↗](/cve/CVE-2026-1234)"
// We use a special label "↗" so the renderer can detect and replace it
// with a styled SVG icon.
function linkifyCves(text) {
  if (!text) return text;
  const placeholders = [];
  const mask = (match) => {
    placeholders.push(match);
    return `\x00${placeholders.length - 1}\x00`;
  };
  let masked = text
    .replace(/```[\s\S]*?```/g, mask)
    .replace(/`[^`\n]+`/g, mask)
    .replace(/!?\[[^\]]*\]\([^)]+\)/g, mask)
    .replace(/<[^>]+>/g, mask);

  // Plain text CVE ID, followed by a markdown link with a sentinel label.
  masked = masked.replace(CVE_RE, (id) => `${id}[↗](/cve/${id})`);

  return masked.replace(/\x00(\d+)\x00/g, (_, i) => placeholders[Number(i)]);
}

function ExternalIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: "middle", marginLeft: 2, marginRight: 1 }}
      aria-hidden="true">
      <path d="M7 17 17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

export default function NewsPanel({ isAdmin = false }) {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewsList()
      .then(data => {
        setReports(data);
        setLoading(false);
        if (data.length > 0) setSelected(data[0].file);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) { setContent(""); return; }
    fetchNewsReport(selected)
      .then(d => setContent(d.content))
      .catch(() => setContent("Failed to load report."));
  }, [selected]);

  const linkedContent = useMemo(() => linkifyCves(content), [content]);

  const handleDelete = async (e, filename) => {
    e.stopPropagation();
    const r = reports.find(x => x.file === filename);
    const label = r ? fmtTs(r.generated_at) : filename;
    if (!window.confirm(`Delete news report from ${label}?`)) return;
    try {
      await deleteNewsReport(filename);
      const remaining = reports.filter(x => x.file !== filename);
      setReports(remaining);
      if (selected === filename) {
        setSelected(remaining.length ? remaining[0].file : null);
      }
    } catch (err) {
      window.alert("Delete failed: " + (err?.message || err));
    }
  };

  if (loading) return <div style={{ padding: 24, color: "var(--text-secondary)" }}>Loading reports...</div>;
  if (!reports.length) return <div style={{ padding: 24, color: "var(--text-secondary)" }}>No news reports yet. The first report runs ~3 min after vinnied starts.</div>;

  return (
    <div style={{ display: "flex", gap: 16, padding: "0 16px" }}>
      <div style={{ width: 280, flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8 }}>Reports</div>
        <div style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {reports.map(r => {
            const label = fmtTs(r.generated_at);
            return (
              <div
                key={r.file}
                onClick={() => setSelected(r.file)}
                style={{
                  padding: "10px 12px", borderRadius: 6, cursor: "pointer", marginBottom: 4,
                  border: selected === r.file ? "1px solid var(--accent)" : "1px solid var(--border)",
                  background: selected === r.file ? "rgba(88,166,255,0.06)" : "var(--bg-secondary)",
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--text-primary)", fontSize: 13 }}>{label}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>
                    {r.model} | {r.elapsed}s | {r.input_tokens?.toLocaleString()}→{r.output_tokens?.toLocaleString()} tok
                  </div>
                </div>
                {isAdmin && (
                  <button
                    onClick={(e) => handleDelete(e, r.file)}
                    title="Delete report (admin)"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text-muted)",
                      cursor: "pointer",
                      padding: 4,
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.color = "var(--severity-critical)"; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{
        flex: 1, minWidth: 0,
        background: "var(--bg-secondary)",
        border: "1px solid var(--border)",
        borderRadius: 6, padding: 24,
        overflowY: "auto", maxHeight: "85vh",
        color: "var(--text-primary)",
      }}>
        <div className="news-markdown">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, href, children, ...props }) => {
                const isInternal = href && href.startsWith("/");
                // CVE link sentinel: render as an icon-only link after the CVE text.
                const isCveSentinel = isInternal && href.startsWith("/cve/") &&
                  Array.isArray(children) && children.length === 1 && children[0] === "↗";
                if (isCveSentinel) {
                  return (
                    <a href={href} title={`Search ${href.replace("/cve/", "")}`}
                      style={{ color: "var(--accent)", textDecoration: "none" }}>
                      <ExternalIcon />
                    </a>
                  );
                }
                return <a {...props} href={href}
                  target={isInternal ? "_self" : "_blank"}
                  rel={isInternal ? undefined : "noopener noreferrer"}
                  style={{ color: "var(--accent)" }}>{children}</a>;
              },
              table: ({ node, ...props }) => <table {...props} style={{ borderCollapse: "collapse", margin: "12px 0", fontSize: 13 }} />,
              th: ({ node, ...props }) => <th {...props} style={{ borderBottom: "1px solid var(--border)", padding: "6px 10px", textAlign: "left", color: "var(--text-secondary)" }} />,
              td: ({ node, ...props }) => <td {...props} style={{ borderBottom: "1px solid var(--border)", padding: "6px 10px" }} />,
              code: ({ node, inline, ...props }) => inline
                ? <code {...props} style={{ background: "var(--bg-card)", padding: "1px 4px", borderRadius: 3, fontSize: "0.9em" }} />
                : <code {...props} />,
              pre: ({ node, ...props }) => <pre {...props} style={{ background: "var(--bg-card)", padding: 12, borderRadius: 6, overflowX: "auto", fontSize: 12, color: "var(--text-primary)" }} />,
              h1: ({ node, ...props }) => <h1 {...props} style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--border)", paddingBottom: 8, marginTop: 24 }} />,
              h2: ({ node, ...props }) => <h2 {...props} style={{ color: "var(--text-primary)", marginTop: 20 }} />,
              h3: ({ node, ...props }) => <h3 {...props} style={{ color: "var(--text-primary)", marginTop: 16 }} />,
            }}
          >
            {linkedContent}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
